<div class="flex flex-col space-y-4">
  <!-- Header + registration type -->
  <div>
    <h2 class="text-sm font-bold text-slate-800 flex items-center gap-2 mb-1">
      <mat-icon class="text-blue-600 text-lg">groups</mat-icon>
      Register company &amp; members
    </h2>
    <p class="text-xs text-slate-400">
      @if (parkingSpaceName()) {
        Creates long-term rights for members of a company at {{ parkingSpaceName() }}.
      } @else {
        Creates an organization and long-term rights for its members.
      }
    </p>
  </div>

  <!-- Type toggle -->
  <div class="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium">
    @for (t of ['Supplier', 'Employee', 'Student']; track t) {
      <button type="button" (click)="setType($any(t))"
        class="px-3 py-1.5 rounded-md transition"
        [ngClass]="registrationType() === t ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
        {{ t }}
      </button>
    }
  </div>

  <!-- Company search -->
  <div>
    <label class="text-xs font-medium text-slate-500 mb-1 block">Company</label>

    @if (isCompanyLocked()) {
      <!-- This site belongs to a single organization — Employee/Student registrations are always
           for that company, so the field is locked rather than offered as a choice. -->
      <div class="flex items-center gap-2 border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-slate-100 text-slate-500">
        <mat-icon class="text-base text-slate-400">lock</mat-icon>
        <span class="truncate">{{ companyName() }}</span>
      </div>
    } @else {
    <div class="flex gap-2">
      <input type="text" [ngModel]="companyName()" (ngModelChange)="onCompanyNameChange($event)"
        (keyup.enter)="searchCompany()" placeholder="Company name"
        class="flex-1 border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <button type="button" (click)="searchCompany()" [disabled]="isSearching()"
        class="inline-flex items-center justify-center px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-blue-600 hover:bg-slate-50 transition disabled:opacity-50">
        @if (isSearching()) { <mat-spinner diameter="16"></mat-spinner> } @else { <mat-icon class="text-lg">search</mat-icon> }
      </button>
    </div>
    }

    @if (matchedOrg(); as org) {
      <div class="mt-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1">
        <div class="flex items-center gap-2">
          <mat-icon class="text-sm">check_circle</mat-icon>
          <span class="truncate">Using existing organization “{{ org.name }}”.</span>
          @if (!isCompanyLocked()) {
            <button type="button" (click)="clearMatchedOrg()" class="ml-auto shrink-0 text-emerald-600 hover:text-emerald-800">Change</button>
          }
        </div>
        <!-- Stored company details, so the operator can confirm this is the right company before
             adding members to it. Read-only here — editing them lives in the registry's edit dialog. -->
        <div class="mt-0.5 pl-6 space-y-0.5 text-emerald-600">
          @if (orgContact(org)) {
            <p class="truncate">{{ orgContact(org) }}</p>
          } @else {
            <p class="italic opacity-80">No contact details stored.</p>
          }
          @if (orgAddress(org)) { <p class="truncate">{{ orgAddress(org) }}</p> }
          @if (orgMembersLabel(org)) { <p>{{ orgMembersLabel(org) }}</p> }
        </div>
      </div>
    } @else if (companyResults().length) {
      <div class="mt-1.5 border border-slate-100 rounded-lg divide-y divide-slate-100">
        @for (org of companyResults(); track org.id) {
          <button type="button" (click)="useOrg(org)"
            class="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-start gap-2">
            <mat-icon class="shrink-0 mt-0.5 text-sm text-slate-400">business</mat-icon>
            <span class="min-w-0">
              <span class="block text-xs font-medium text-slate-700 truncate">{{ org.name }}</span>
              <!-- Contact / address / member count come from the search response itself; they are
                   what tells two similarly named companies apart. -->
              @if (orgContact(org)) {
                <span class="block text-[11px] text-slate-500 truncate">{{ orgContact(org) }}</span>
              } @else {
                <span class="block text-[11px] text-slate-400 italic">No contact stored</span>
              }
              @if (orgAddress(org)) {
                <span class="block text-[11px] text-slate-400 truncate">{{ orgAddress(org) }}</span>
              }
              @if (orgMembersLabel(org)) {
                <span class="block text-[11px] text-slate-400">{{ orgMembersLabel(org) }}</span>
              }
            </span>
          </button>
        }
      </div>
    } @else if (companyName().trim()) {
      <p class="mt-1.5 text-xs text-slate-400 italic">No match — a new organization “{{ companyName() }}” will be created.</p>
    }
  </div>

  <!-- Company contact info (only needed when creating a new org) -->
  @if (!matchedOrg()) {
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label class="text-xs font-medium text-slate-500 mb-1 block">Contact person <span class="text-slate-300">(optional)</span></label>
        <input type="text" [ngModel]="contactPerson()" (ngModelChange)="contactPerson.set($event)"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label class="text-xs font-medium text-slate-500 mb-1 block">Contact phone <span class="text-slate-300">(optional)</span></label>
        <input type="tel" [ngModel]="contactPhone()" (ngModelChange)="contactPhone.set($event)"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div class="sm:col-span-2">
        <label class="text-xs font-medium text-slate-500 mb-1 block">Company address <span class="text-slate-300">(optional)</span></label>
        <input type="text" [ngModel]="addressLine()" (ngModelChange)="addressLine.set($event)" placeholder="Street and number"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <input type="text" [ngModel]="addressPostCode()" (ngModelChange)="addressPostCode.set($event)" placeholder="Post code"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <input type="text" [ngModel]="addressLocality()" (ngModelChange)="addressLocality.set($event)" placeholder="City"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div class="sm:col-span-2">
        <label class="text-xs font-medium text-slate-500 mb-1 block">Country</label>
        <select [ngModel]="addressCountry()" (ngModelChange)="addressCountry.set($event)"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          @for (c of countries; track c.code) {
            <option [value]="c.code">{{ c.name }} ({{ c.code }})</option>
          }
        </select>
      </div>
    </div>
  }

  <!-- Members -->
  <div>
    <div class="flex items-center justify-between mb-1.5">
      <label class="text-xs font-medium text-slate-500">Members</label>
      <button type="button" (click)="addMember()"
        class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
        <mat-icon class="text-base">add</mat-icon> Add member
      </button>
    </div>

    <div class="space-y-2">
      @for (member of members(); track $index) {
        <div class="border border-slate-100 rounded-lg p-2.5 space-y-2 bg-slate-50/50">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" [ngModel]="member.firstName" (ngModelChange)="updateMember($index, { firstName: $event })"
              placeholder="First name"
              class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input type="text" [ngModel]="member.lastName" (ngModelChange)="updateMember($index, { lastName: $event })"
              placeholder="Last name"
              class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <input type="text" [ngModel]="member.plates" (ngModelChange)="updateMember($index, { plates: $event })"
            placeholder="License plate(s) — separate multiple with a comma"
            class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <!--
            Optional, but the hint is not decoration: the member's login is matched on this address,
            so leaving it blank means a placeholder is generated and the person cannot later reach
            the account created for them.
          -->
          <input type="email" [ngModel]="member.email" (ngModelChange)="updateMember($index, { email: $event })"
            placeholder="Email (optional — lets them use the webshop later)"
            autocomplete="off"
            class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div class="grid grid-cols-1 gap-2" [class.sm:grid-cols-2]="usesAkNumber()">
            @if (usesAkNumber()) {
              <input type="text" [ngModel]="member.akNumber" (ngModelChange)="updateMember($index, { akNumber: $event })"
                placeholder="AK number (optional)"
                class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            }
            <div class="flex items-center gap-2">
              <input type="tel" [ngModel]="member.phone" (ngModelChange)="updateMember($index, { phone: $event })"
                placeholder="Member phone (optional)"
                class="flex-1 border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              @if (members().length > 1) {
                <button type="button" (click)="removeMember($index)" title="Remove member"
                  class="shrink-0 text-slate-400 hover:text-red-600 transition flex items-center">
                  <mat-icon class="text-lg">delete_outline</mat-icon>
                </button>
              }
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Members already registered for the selected company. Shown so the operator can see who is
         on file before adding anyone — re-registering an existing member creates a second parker
         and a second right for the same person. Read-only; editing lives in the registry below. -->
    @if (matchedOrg(); as org) {
      <div class="mt-3 border-t border-slate-100 pt-2">
        <div class="flex items-center gap-1.5 mb-1.5">
          <span class="text-xs font-medium text-slate-500">Already registered at {{ org.name }}</span>
          @if (isLoadingMembers()) {
            <mat-spinner diameter="12"></mat-spinner>
          } @else if (orgMembers().length) {
            <span class="text-[11px] text-slate-400">({{ orgMembers().length }})</span>
          }
        </div>

        @if (!isLoadingMembers()) {
          @if (orgMembers().length) {
            <div class="space-y-1 max-h-56 overflow-y-auto pr-0.5">
              @for (member of orgMembers(); track member.id) {
                <div class="flex items-center gap-2 border border-slate-100 rounded-lg px-2 py-1.5 bg-white">
                  <mat-icon class="shrink-0 text-base text-slate-300">person</mat-icon>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5">
                      @if (memberName(member)) {
                        <span class="text-xs font-medium text-slate-700 truncate">{{ memberName(member) }}</span>
                      } @else {
                        <span class="text-xs italic text-slate-400 truncate">Unnamed member</span>
                      }
                      @if (isOrgAdmin(member)) {
                        <span class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">Admin</span>
                      }
                    </div>
                    @if (memberPlates(member).length) {
                      <div class="flex flex-wrap items-center gap-1 mt-0.5">
                        @for (plate of memberPlates(member); track plate) {
                          <span class="font-mono text-[10px] font-semibold bg-slate-800 text-white px-1.5 py-0.5 rounded">{{ plate }}</span>
                        }
                      </div>
                    } @else {
                      <span class="text-[11px] italic text-slate-400">No license plate</span>
                    }
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="text-xs text-slate-400 italic">No members registered yet.</p>
          }
        }
      </div>
    }
  </div>

  <!-- Zones (optional) -->
  @if (zones().length) {
    <div>
      <div class="flex items-center justify-between mb-1">
        <label class="text-xs font-medium text-slate-500 block">Zones <span class="text-slate-300">(optional — all if none)</span></label>
        <button type="button" (click)="toggleAllZones()" class="text-xs font-medium text-blue-600 hover:text-blue-700 transition">
          {{ allZonesSelected() ? 'Clear' : 'Select all' }}
        </button>
      </div>
      <div class="flex flex-wrap gap-1.5">
        @for (zone of zones(); track zone.id) {
          <button type="button" (click)="toggleZone(zone.id)"
            class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border transition"
            [ngClass]="isZoneSelected(zone.id) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'">
            <mat-icon class="text-[14px] w-3.5 h-3.5 leading-none">{{ isZoneSelected(zone.id) ? 'check' : 'location_on' }}</mat-icon>
            {{ zone.name || 'Zone' }}
          </button>
        }
      </div>
    </div>
  }

  <!-- Valid until -->
  <div>
    <label class="text-xs font-medium text-slate-500 mb-1 block">Valid until <span class="text-slate-300">(optional)</span></label>
    <app-date-field [(value)]="validTo" />
  </div>

  <button type="button" (click)="submit()" [disabled]="isSubmitting() || !parkingSpaceId()"
    class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2 transition">
    @if (isSubmitting()) {
      <mat-spinner diameter="18"></mat-spinner> Registering…
    } @else {
      <mat-icon class="text-lg">how_to_reg</mat-icon> Register {{ registrationType() }}{{ registrationType() === 'Supplier' ? '' : ' member(s)' }}
    }
  </button>
</div>
